import { ActivityType, ActivityTypeId } from '@/lib/api/company/chartScan/types'

export const CHART_ADDING_MODES: {
  type: ActivityTypeId
  digit: string
  color: string
}[] = [
  { type: ActivityType.Driving, digit: '1', color: 'rgb(218, 37, 29)' },
  { type: ActivityType.Work, digit: '2', color: 'rgb(231, 120, 23)' },
  { type: ActivityType.Availability, digit: '3', color: 'rgb(255, 218, 0)' },
  { type: ActivityType.Rest, digit: '4', color: 'rgb(117, 197, 240)' },
  { type: ActivityType.NoData, digit: '5', color: '#94a3b8' },
]

const DIGIT_TO_MODE: Record<string, ActivityTypeId> = Object.fromEntries(
  CHART_ADDING_MODES.map((m) => [m.digit, m.type]),
)

export type ShortcutKeyEvent = {
  key: string
  code: string
  ctrlKey: boolean
  metaKey: boolean
  altKey?: boolean
  shiftKey?: boolean
}

export type ShortcutTarget = {
  tagName?: string
  isContentEditable?: boolean
} | null

export type ChartShortcutAction =
  | { kind: 'setMode'; mode: ActivityTypeId | null }
  | { kind: 'deleteSelected' }
  | { kind: 'deleteAllOfType'; type: ActivityTypeId }
  | { kind: 'undo' }
  | { kind: 'redo' }

export function resolveChartEditorShortcut(
  e: ShortcutKeyEvent,
  target: ShortcutTarget,
  currentMode: ActivityTypeId | null,
): ChartShortcutAction | null {
  if (isTypingTarget(target)) return null

  const ctrl = e.ctrlKey || e.metaKey
  if (ctrl && (e.key === 'z' || e.key === 'Z')) return { kind: 'undo' }
  if (ctrl && (e.key === 'y' || e.key === 'Y')) return { kind: 'redo' }

  // Digit 0..5 with or without Ctrl. Chrome keeps Ctrl+1..8 for tabs, so the
  // unchorded digit is the binding that actually reaches the page.
  if (!e.altKey && !e.shiftKey) {
    const digit = digitFromEvent(e)
    if (digit === '0') return { kind: 'setMode', mode: null }
    const mode = digit != null ? DIGIT_TO_MODE[digit] : undefined
    if (mode != null) {
      return { kind: 'setMode', mode: currentMode === mode ? null : mode }
    }
  }

  if (e.key === 'Delete') return { kind: 'deleteSelected' }
  if (e.key === 'F2') return { kind: 'deleteAllOfType', type: ActivityType.Work }
  if (e.key === 'F3')
    return { kind: 'deleteAllOfType', type: ActivityType.Availability }
  if (e.key === 'F4') return { kind: 'deleteAllOfType', type: ActivityType.Driving }
  return null
}

function isTypingTarget(target: ShortcutTarget): boolean {
  if (target == null) return false
  const tag = target.tagName
  return (
    tag === 'INPUT' ||
    tag === 'TEXTAREA' ||
    tag === 'SELECT' ||
    target.isContentEditable === true
  )
}

function digitFromEvent(e: ShortcutKeyEvent): string | null {
  const fromCode = /^Digit([0-9])$/.exec(e.code)
  if (fromCode) return fromCode[1]
  if (e.key.length === 1 && e.key >= '0' && e.key <= '9') return e.key
  return null
}
